'use client';

import {
	SLOT_DURATION_BASELINE,
	SLOT_DURATION_FLOOR,
	SlotClock,
	SlotDurationMs,
	currentSlotClock,
} from '@velocity-exchange/sdk';
import { useMemo } from 'react';
import useVelocityClient from 'src/hooks/useVelocityClient';
import { useSlotQuery } from 'src/hooks/query/slot/useSlotQuery';

/**
 * Solana's slot length is dropping 400 -> 350 -> 300 -> 250 -> 200ms, so no
 * client may hold a slot length. Every slot/wall-clock conversion in the UI
 * reads the live `State.slotDurationMs` through these hooks.
 *
 * When the feed is live both hooks return the same measured value. They differ
 * only in which way they guess when it is not, and there is no single safe
 * guess: a risk ceiling wants the longest slot so it tightens, while a window
 * a human races wants the shortest so it under-promises the time left.
 *
 * Resolving the live value needs a slot, only to tell whether a staged flip has
 * activated. Without one the duration on `State` is still readable and beats a
 * blind constant, so each hook prefers it and leans its own way: staged flips
 * only ever shorten, so the pending value is the shortest the chain can already
 * be running and the active value is the longest.
 */

type StateDurations = { active?: SlotDurationMs; pending?: SlotDurationMs };

const asDuration = (value: unknown): SlotDurationMs | undefined =>
	typeof value === 'number' && Number.isFinite(value) && value > 0
		? (value as SlotDurationMs)
		: undefined;

function useSlotClockSource() {
	const velocityClient = useVelocityClient();
	const { data: slotState } = useSlotQuery();

	// A failed subscription reports `{currentSlot: 0, isSubscribed: false}`, and
	// slot 0 precedes every effective slot, so it would resolve to the pre-flip
	// base while looking live. Never pass it through as a slot.
	const currentSlot = slotState?.isSubscribed
		? slotState.currentSlot
		: undefined;

	return useMemo(() => {
		if (!velocityClient) {
			return { clock: undefined, durations: {} as StateDurations };
		}

		let durations: StateDurations = {};
		try {
			const state = velocityClient.getStateAccount();
			durations = {
				active: asDuration(state.slotDurationMs),
				pending: asDuration(state.pendingSlotDurationMs),
			};
		} catch {
			// Not subscribed yet: the client throws rather than returning undefined.
		}

		return { clock: currentSlotClock(velocityClient, currentSlot), durations };
	}, [velocityClient, currentSlot]);
}

/**
 * For windows that protect the user: swift signing budgets, blockhash and
 * auction countdowns, anything whose expiry a human races. Under-promises the
 * time remaining when the live duration is unavailable.
 */
export function useUserProtectionSlotClock(): SlotClock {
	const { clock, durations } = useSlotClockSource();

	return useMemo(() => {
		if (clock?.isLive) return clock;

		return {
			slotDurationMs:
				durations.pending ?? durations.active ?? SLOT_DURATION_FLOOR,
			isLive: false,
		};
	}, [clock, durations]);
}

/**
 * For ceilings that protect against bad data: oracle and DLOB staleness,
 * account freshness, refetch pacing. Tightens when the live duration is
 * unavailable, so a longer slot is the safe assumption.
 */
export function useRiskCeilingSlotClock(): SlotClock {
	const { clock, durations } = useSlotClockSource();

	return useMemo(() => {
		if (clock?.isLive) return clock;

		return {
			slotDurationMs: durations.active ?? SLOT_DURATION_BASELINE,
			isLive: false,
		};
	}, [clock, durations]);
}
